import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { ModalSize } from '../../../_metronic/helpers'

export type FetchState<T = any> = {
  data: T | undefined
  isLoading: boolean
  error: any
  fetcher?: () => Promise<T>
}

type ModalContextType = {
  isOpen: boolean
  showModal: (data: IModal) => void
  closeModal: () => void
  modalContent: ReactNode | null
  modalSize?: ModalSize
  modalTitle: string
  alignLeft?: boolean
  disableClose?: boolean
  centered?: boolean
  darkBackdrop?: boolean
  modalOnHide?: () => void
  setModalOnHide?: Dispatch<SetStateAction<(() => void) | undefined>>
  /**
   * Guarda opcional de fechamento: quando registrada, o X, o Esc e o clique no
   * backdrop consultam-na antes de fechar. Devolvendo `false`, o modal
   * **não** fecha e quem registrou assume a responsabilidade de confirmar com
   * o usuário e chamar `closeModal()`. Sem guarda registrada nada muda — é
   * opt-in, para telas que perderiam dado ao fechar no meio de uma operação.
   */
  guardaFechamento?: () => boolean
  registrarGuardaFechamento: (guarda?: () => boolean) => void
  fetchStates: { [key: string]: FetchState }
  refetch: (key: string) => void
  setData: (key: string, data: any) => void
  persistData: (key: string, data: any) => void
  getPersistedData: (key: string) => any
  /** Linha de apoio abaixo do título, no cabeçalho padrão (contexto: de quem/de onde é o dado). */
  modalDescription?: string
  /** Classe aplicada ao `.modal-content` (react-bootstrap `contentClassName`), para dialogs com chrome próprio. */
  contentClassName?: string
}

export type IModal = {
  title: string
  content?: ReactNode
  size?: ModalSize
  alignLeft?: boolean
  disableClose?: boolean
  centered?: boolean
  darkBackdrop?: boolean
  fetchers?: { [key: string]: () => Promise<any> }
  onClose?: () => void
  description?: string
  contentClassName?: string
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)
export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modalContent, setModalContent] = useState<ReactNode | null>(null)
  const [modalSize, setModalSize] = useState<ModalSize>(ModalSize.Large)
  const [modalTitle, setModalTitle] = useState<string>('')
  const [modalOnHide, setModalOnHide] = useState<(() => void) | undefined>(undefined)
  const [alignLeft, setAlignLeft] = useState<boolean>(false)
  const [disableClose, setDisableClose] = useState(false)
  const [centered, setCentered] = useState(false)
  const [darkBackdrop, setDarkBackdrop] = useState(false)
  const [modalDescription, setModalDescription] = useState<string | undefined>(undefined)
  const [contentClassName, setContentClassName] = useState<string | undefined>(undefined)
  const [persistedData, setPersistedData] = useState<{ [key: string]: any }>({})
  const [guardaFechamento, setGuardaFechamento] = useState<(() => boolean) | undefined>(undefined)

  const registrarGuardaFechamento = useCallback((guarda?: () => boolean) => {
    // `useState` trata função como updater: o wrapper é obrigatório.
    setGuardaFechamento(() => guarda)
  }, [])

  const [fetchStates, setFetchStates] = useState<{ [key: string]: FetchState }>({})

  // Limpeza que o `closeModal` agenda para depois da animação de saída. Um `showModal` logo em
  // seguida (fluxo encadeado: formulário → CID → e-SUS) precisa cancelá-la, senão ela roda depois e
  // apaga o modal novo, que abre em branco.
  const limpezaPendente = useRef<ReturnType<typeof setTimeout>>()

  const limparModal = useCallback(() => {
    setAlignLeft(false)
    setDisableClose(false)
    setCentered(false)
    setDarkBackdrop(false)
    setModalDescription(undefined)
    setContentClassName(undefined)
    setModalOnHide(undefined)
    setFetchStates({})
    setModalSize(ModalSize.Large)
    setModalContent(null)
  }, [])

  const persistData = useCallback((key: string, data: any) => {
    setPersistedData((prev) => ({ ...prev, [key]: data }))
  }, [])

  const getPersistedData = useCallback((key: string) => persistedData[key] || null, [persistedData])

  const fetchData = useCallback(async (key: string, fetcher: () => Promise<any>) => {
    setFetchStates((prev) => ({
      ...prev,
      [key]: { ...prev[key], isLoading: true, data: undefined, error: undefined, fetcher },
    }))

    try {
      const data = await fetcher()
      setFetchStates((prev) => ({
        ...prev,
        [key]: { ...prev[key], isLoading: false, data, error: undefined, fetcher },
      }))
    } catch (error) {
      setFetchStates((prev) => ({
        ...prev,
        [key]: { ...prev[key], isLoading: false, data: undefined, error, fetcher },
      }))
    }
  }, [])

  const refetch = useCallback(
    (key: string) => {
      const fetcher = fetchStates[key]?.fetcher
      if (fetcher) {
        fetchData(key, fetcher)
      }
    },
    [fetchData, fetchStates]
  )

  const setData = useCallback((key: string, data: any) => {
    setFetchStates((prev) => ({
      ...prev,
      [key]: { ...prev[key], data, isLoading: false, error: undefined },
    }))
  }, [])

  const showModal = useCallback(
    async ({
      title,
      content,
      size,
      fetchers,
      onClose,
      alignLeft = false,
      disableClose = false,
      centered = false,
      darkBackdrop = false,
      description,
      contentClassName,
    }: IModal) => {
      // Fechamento ainda animando: limpa agora o que ele limparia depois, para o modal novo não
      // herdar tamanho, alinhamento e dados do anterior.
      if (limpezaPendente.current) {
        clearTimeout(limpezaPendente.current)
        limpezaPendente.current = undefined
        limparModal()
      }
      setModalTitle(title)
      if (size) setModalSize(size)
      if (alignLeft) setAlignLeft(alignLeft)
      setDisableClose(disableClose)
      setCentered(centered)
      setDarkBackdrop(darkBackdrop)
      setModalDescription(description)
      setContentClassName(contentClassName)
      setModalOnHide(() => onClose || undefined)
      setModalContent(content || null)

      if (fetchers) {
        Object.entries(fetchers).forEach(([key, fetcher]) => {
          fetchData(key, fetcher)
        })
      }

      setIsOpen(true)
    },
    [fetchData, limparModal]
  )

  const closeModal = useCallback(() => {
    setIsOpen(false)
    modalOnHide?.()
    setGuardaFechamento(undefined)

    limpezaPendente.current = setTimeout(() => {
      limpezaPendente.current = undefined
      limparModal()
    }, 300)
  }, [modalOnHide, limparModal])

  const contextValue = useMemo(
    () => ({
      isOpen,
      showModal,
      closeModal,
      modalContent,
      modalSize,
      modalTitle,
      alignLeft,
      disableClose,
      centered,
      darkBackdrop,
      modalDescription,
      contentClassName,
      fetchStates,
      refetch,
      setData,
      persistData,
      getPersistedData,
      guardaFechamento,
      registrarGuardaFechamento,
    }),
    [
      isOpen,
      showModal,
      closeModal,
      modalContent,
      modalSize,
      modalTitle,
      alignLeft,
      disableClose,
      centered,
      darkBackdrop,
      modalDescription,
      contentClassName,
      fetchStates,
      refetch,
      setData,
      persistData,
      getPersistedData,
      guardaFechamento,
      registrarGuardaFechamento,
    ]
  )

  return <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>
}
export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}
